/**
 * NOVA ✦ 2.0 — dein Lern-Buddy  ///  now ACTUALLY alive
 * ─────────────────────────────────────────────────────────────────────────────
 * v2 "Living Being" upgrade:
 *   · EYES THAT SEE YOU — pupils track the mouse cursor across the whole
 *     screen (rAF-throttled), with idle look-arounds when you're away.
 *   · REAL LOCOMOTION — Nova HOPS while traveling between anchors (squash &
 *     stretch), leaves a sparkle trail, lands with a squish.
 *   · ARMS! — stubby little arms that wave on hover and on greetings.
 *   · EMOTES — after landing Nova sometimes waves, hearts, sparkles or
 *     dozes off (💤 at long idle).
 *   · UI-AWARE — anchor-bound tips that point at real Core features
 *     ("Drück ⌘K!", "1–8 wechselt die Welten!").
 *   · Bigger everywhere (116px wandering / 84px docked) — presence, not pixel.
 * Chat panel & customizer unchanged (they were approved).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/api/client";
import { useAuthStore } from "@/store/authStore";

/* ── customization catalog ──────────────────────────────────────────────── */
const COLORS = ["#8b5cf6", "#22d3ee", "#22c55e", "#fbbf24", "#ec4899", "#f97316", "#38bdf8", "#f43f5e"];
/* eyes/acc leben weiter im gespeicherten Look (Server-Kompat), aber Novas
   Körper ist jetzt das gerenderte Sprite-Asset — customizbar bleibt die Aura. */
const STYLES = [
  { id: "modern",          label: "Locker & Gen Z" },
  { id: "direkt",          label: "Direkt & kompakt" },
  { id: "erklaerer",       label: "Schritt für Schritt" },
  { id: "coach",           label: "Hype-Coach" },
  { id: "wissenschaftler", label: "Wissenschaftlich" },
];
const LOOK_KEY = "studox-nova-look-v1";
const readLook = () => {
  try { return { color: "#8b5cf6", eyes: "round", acc: "none", ...(JSON.parse(localStorage.getItem(LOOK_KEY) || "{}")) }; }
  catch { return { color: "#8b5cf6", eyes: "round", acc: "none" }; }
};

/* generic teasers + Core-UI tips bound to anchors */
const TEASERS = [
  "Yo! Brauchst du Hilfe? ✦",
  "Frag mich alles — fr!",
  "Lern-Tipp gefällig? 👀",
  "Ich glaub an dich. Ehrlich.",
  "Zusammen sind wir Gigabrain 🧠",
  "Kleine Pause? Oder Grind? 🔥",
];
const ANCHORS = [
  { left: "3%",  bottom: "5%",  tip: "Links ist die Welten-Leiste — 1 bis 8 wechselt blitzschnell! ⚡" },
  { left: "22%", bottom: "3.5%", tip: null },
  { left: "46%", bottom: "3%",  tip: "Drück ⌘K und starte jede App sofort! 🚀" },
  { left: "70%", bottom: "4%",  tip: null },
  { left: "88%", bottom: "8%",  tip: "Der Hilfe-Button oben ist IMMER für dich da 💜" },
];
const EMOTES = ["👋", "💜", "✨", "🎵", "⭐"];

/* ═══ THE AVATAR — a living SVG being ════════════════════════════════════ */
export function NovaAvatar({ size = 96, look, mood = "idle", facing = 1, eye = { x: 0, y: 0 }, wave = false, traveling = false }) {
  const { color, eyes, acc } = look;
  const u = `nv-${color.replace("#", "")}`;
  const ex = Math.max(-3, Math.min(3, eye.x));
  const ey = Math.max(-2.4, Math.min(2.4, eye.y));
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ overflow: "visible", transform: `scaleX(${facing})`, transition: "transform 0.35s ease" }}>
      <defs>
        <radialGradient id={`${u}-body`} cx="36%" cy="28%" r="80%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="38%" stopColor={color} />
          <stop offset="100%" stopColor="#140b30" />
        </radialGradient>
        <radialGradient id={`${u}-aura`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.5" />
          <stop offset="70%" stopColor={color} stopOpacity="0.1" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* aura + double orbiting sparks */}
      <circle cx="50" cy="52" r="47" fill={`url(#${u}-aura)`}>
        <animate attributeName="opacity" values="0.8;1;0.8" dur="2.6s" repeatCount="indefinite" />
      </circle>
      <g>
        <animateTransform attributeName="transform" type="rotate" from="0 50 52" to="360 50 52" dur="6s" repeatCount="indefinite" />
        <circle cx="50" cy="9" r="2.4" fill="#fff" opacity="0.9" />
        <circle cx="50" cy="9" r="4.8" fill={color} opacity="0.35" />
      </g>
      <g>
        <animateTransform attributeName="transform" type="rotate" from="180 50 52" to="540 50 52" dur="9s" repeatCount="indefinite" />
        <circle cx="50" cy="12" r="1.5" fill="#fde047" opacity="0.85" />
      </g>

      {/* squash & stretch body */}
      <g style={{
        transformOrigin: "50px 82px",
        transform: traveling ? "scale(0.94, 1.08)" : "scale(1,1)",
        transition: "transform 0.3s ease",
      }}>
        <g>
          <animateTransform attributeName="transform" type="scale" values="1 1; 1.02 0.98; 1 1" additive="sum" dur="2.4s" repeatCount="indefinite" />
          <circle cx="50" cy="52" r="31" fill={`url(#${u}-body)`} />
          <circle cx="50" cy="52" r="31" fill="none" stroke="rgba(255,255,255,0.42)" strokeWidth="1" />
          <ellipse cx="40.5" cy="39" rx="10.5" ry="6.2" fill="#fff" opacity="0.3" transform="rotate(-24 40.5 39)" />
          <ellipse cx="50" cy="85" rx="15" ry="3" fill={color} opacity="0.25" />
        </g>

        {/* ── ARMS — stubby, wave on demand ── */}
        <g style={{ transformOrigin: "26px 58px", animation: wave ? "nova-wave 0.7s ease-in-out infinite" : "none" }}>
          <path d="M24 58 q-8 -3 -10 -10" fill="none" stroke={color} strokeWidth="6.5" strokeLinecap="round" opacity="0.95" />
          <circle cx="14" cy="47" r="4.4" fill="#fff" opacity="0.9" />
        </g>
        <path d="M76 58 q8 -1 10 -6" fill="none" stroke={color} strokeWidth="6.5" strokeLinecap="round" opacity="0.9" />

        {/* ── FACE ── */}
        {/* blink wrapper */}
        <g>
          <animateTransform attributeName="transform" type="scale" values="1 1;1 1;1 1;1 0.06;1 1;1 1;1 1;1 0.06;1 0.06;1 1;1 1" keyTimes="0;0.36;0.39;0.41;0.43;0.72;0.75;0.77;0.79;0.81;1" dur="6.2s" repeatCount="indefinite" additive="sum" style={{ transformOrigin: "50px 50px" }} />
          {/* eye whites — big, expressive */}
          {eyes !== "chill" && eyes !== "happy" && (<>
            <ellipse cx="40" cy="49" rx="7" ry="8" fill="#fff" />
            <ellipse cx="60" cy="49" rx="7" ry="8" fill="#fff" />
          </>)}
          {/* pupils that TRACK the cursor */}
          {eyes === "round" && (
            <g style={{ transform: `translate(${ex}px, ${ey}px)`, transition: "transform 0.12s ease-out" }}>
              <circle cx="40" cy="49" r="3.4" fill="#140b30" />
              <circle cx="60" cy="49" r="3.4" fill="#140b30" />
              <circle cx="41.3" cy="47.6" r="1.2" fill="#fff" />
              <circle cx="61.3" cy="47.6" r="1.2" fill="#fff" />
            </g>
          )}
          {eyes === "star" && (
            <g style={{ transform: `translate(${ex * 0.6}px, ${ey * 0.6}px)`, transition: "transform 0.12s ease-out" }}>
              <path d="M40 44 l1.8 3.7 4 0.6 -2.9 2.8 0.7 4 -3.6 -1.9 -3.6 1.9 0.7 -4 -2.9 -2.8 4 -0.6 Z" fill="#fbbf24" />
              <path d="M60 44 l1.8 3.7 4 0.6 -2.9 2.8 0.7 4 -3.6 -1.9 -3.6 1.9 0.7 -4 -2.9 -2.8 4 -0.6 Z" fill="#fbbf24" />
            </g>
          )}
          {eyes === "happy" && (<>
            <path d="M34 50 q6 -7 12 0" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" />
            <path d="M54 50 q6 -7 12 0" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" />
          </>)}
          {eyes === "chill" && (<>
            <path d="M34 48.5 h12" stroke="#fff" strokeWidth="3.8" strokeLinecap="round" />
            <path d="M54 48.5 h12" stroke="#fff" strokeWidth="3.8" strokeLinecap="round" />
            <g style={{ transform: `translate(${ex * 0.5}px, 0px)` }}>
              <circle cx="40" cy="51.5" r="1.8" fill="#fff" opacity="0.75" />
              <circle cx="60" cy="51.5" r="1.8" fill="#fff" opacity="0.75" />
            </g>
          </>)}
        </g>

        {/* mouth by mood */}
        {mood === "talk"
          ? <ellipse cx="50" cy="63" rx="4.4" ry="3.2" fill="#140b30">
              <animate attributeName="ry" values="3.2;1.2;3.2" dur="0.45s" repeatCount="indefinite" />
            </ellipse>
          : mood === "hyped"
            ? <path d="M42 61 q8 9 16 0" fill="none" stroke="#140b30" strokeWidth="3" strokeLinecap="round" />
            : <path d="M44.5 62 q5.5 4.5 11 0" fill="none" stroke="#140b30" strokeWidth="2.6" strokeLinecap="round" />}

        {/* blush */}
        <ellipse cx="32" cy="58" rx="3.6" ry="2.4" fill="#ec4899" opacity="0.4" />
        <ellipse cx="68" cy="58" rx="3.6" ry="2.4" fill="#ec4899" opacity="0.4" />

        {/* ACCESSORIES */}
        {acc === "cap" && (
          <g>
            <path d="M29 33 a21 15 0 0 1 42 0 l0 4 -42 0 Z" fill="#1e1b4b" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
            <path d="M69 35 h15 a3 3 0 0 1 0 6 l-15 -2 Z" fill="#1e1b4b" />
            <circle cx="50" cy="24" r="2.8" fill={color} />
          </g>
        )}
        {acc === "phones" && (
          <g>
            <path d="M25 47 a25 25 0 0 1 50 0" fill="none" stroke="#1e1b4b" strokeWidth="4.6" strokeLinecap="round" />
            <rect x="19" y="44" width="9.5" height="16" rx="4.5" fill="#1e1b4b" stroke={color} strokeWidth="1.5" />
            <rect x="71.5" y="44" width="9.5" height="16" rx="4.5" fill="#1e1b4b" stroke={color} strokeWidth="1.5" />
          </g>
        )}
        {acc === "halo" && (
          <ellipse cx="50" cy="16" rx="16" ry="5" fill="none" stroke="#fde047" strokeWidth="3.2">
            <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
          </ellipse>
        )}
        {acc === "shades" && (
          <g>
            <rect x="31" y="44" width="16" height="10" rx="4" fill="#0b0620" stroke="rgba(255,255,255,0.45)" strokeWidth="1" />
            <rect x="53" y="44" width="16" height="10" rx="4" fill="#0b0620" stroke="rgba(255,255,255,0.45)" strokeWidth="1" />
            <path d="M47 48.5 h6" stroke="rgba(255,255,255,0.45)" strokeWidth="1.8" />
            <path d="M34 46.5 l6 3" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
          </g>
        )}
        {acc === "sprout" && (
          <g>
            <path d="M50 21 q0 -7 0 -9" stroke="#22c55e" strokeWidth="2.6" strokeLinecap="round" fill="none" />
            <path d="M50 13 q-8 -5 -11 1 q7 4 11 -1 Z" fill="#22c55e" />
            <path d="M50 13 q8 -5 11 1 q-7 4 -11 -1 Z" fill="#4ade80" />
          </g>
        )}
      </g>
    </svg>
  );
}

/* ═══ NOVA SPRITE — Novas echter Körper (Nova-Sheet v1: 6×6 = 36 Frames) ═══
   Das gerenderte AAA-Asset ersetzt die SVG-Puppe überall dort, wo Nova
   "in echt" auftritt. Loop: Idle → Winken → Sparkle → Blinzeln.           */
const NOVA_SHEET = "/nova-sheet.png";
const NOVA_COLS = 6, NOVA_ROWS = 6, NOVA_FRAMES = 36, NOVA_AR = 496 / 640;
export function NovaSprite({ size = 96, fps = 11, playing = true, facing = 1, aura = null }) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    if (!playing) return;
    const iv = setInterval(() => setFrame((f) => (f + 1) % NOVA_FRAMES), 1000 / fps);
    return () => clearInterval(iv);
  }, [playing, fps]);
  const col = frame % NOVA_COLS, row = Math.floor(frame / NOVA_COLS);
  const h = Math.round(size * NOVA_AR);
  return (
    <div className="relative" style={{ width: size, height: h }}>
      {aura && (
        <div className="absolute rounded-full" style={{
          inset: `-${Math.round(size * 0.14)}px`,
          background: `radial-gradient(circle at 50% 46%, ${aura}55 0%, ${aura}18 52%, transparent 72%)`,
          filter: "blur(2px)",
          animation: "nova-aura 2.6s ease-in-out infinite",
        }} />
      )}
      <div style={{
        width: size, height: h,
        backgroundImage: `url(${NOVA_SHEET})`,
        backgroundSize: `${NOVA_COLS * 100}% ${NOVA_ROWS * 100}%`,
        backgroundPosition: `${col * (100 / (NOVA_COLS - 1))}% ${row * (100 / (NOVA_ROWS - 1))}%`,
        backgroundRepeat: "no-repeat",
        transform: `scaleX(${facing})`,
        transition: "transform 0.35s ease",
        position: "relative",
      }} />
    </div>
  );
}

/* ═══ THE BUDDY ══════════════════════════════════════════════════════════ */
export default function NovaBuddy({ mode = "dock" }) {
  const user = useAuthStore((s) => s.user);
  const [look, setLook] = useState(readLook);
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(null);
  const [anchor, setAnchor] = useState(mode === "wander" ? 3 : 0);
  const [facing, setFacing] = useState(-1);
  const [hover, setHover] = useState(false);
  const [traveling, setTraveling] = useState(false);
  const [emote, setEmote] = useState(null);
  const [eye, setEye] = useState({ x: 0, y: 0 });
  const btnRef = useRef(null);
  const idleRef = useRef(Date.now());

  /* EYES: pupils follow the cursor, everywhere */
  useEffect(() => {
    let raf = null;
    const onMove = (e) => {
      idleRef.current = Date.now();
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const el = btnRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
        const dx = e.clientX - cx, dy = e.clientY - cy;
        const len = Math.max(1, Math.hypot(dx, dy));
        setEye({ x: (dx / len) * 3, y: (dy / len) * 2.4 });
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => { window.removeEventListener("mousemove", onMove); if (raf) cancelAnimationFrame(raf); };
  }, []);

  /* idle look-around + doze when you're away */
  useEffect(() => {
    const iv = setInterval(() => {
      const idleMs = Date.now() - idleRef.current;
      if (idleMs > 6000) {
        setEye({ x: (Math.random() - 0.5) * 5, y: (Math.random() - 0.5) * 3 });
      }
      if (idleMs > 45000 && !open && Math.random() < 0.5) {
        setEmote("💤");
        setTimeout(() => setEmote(null), 3200);
      }
    }, 2800);
    return () => clearInterval(iv);
  }, [open]);

  /* wander between anchors with hop + landing emote/tip */
  useEffect(() => {
    if (mode !== "wander" || open) return;
    const iv = setInterval(() => {
      setAnchor((cur) => {
        let next;
        do { next = Math.floor(Math.random() * ANCHORS.length); } while (next === cur);
        setFacing(next > cur ? 1 : -1);
        setTraveling(true);
        setTimeout(() => {
          setTraveling(false);
          /* on landing: sometimes emote or drop the anchor's UI tip */
          const roll = Math.random();
          if (ANCHORS[next].tip && roll < 0.45) {
            setTeaser(ANCHORS[next].tip);
            setTimeout(() => setTeaser(null), 4800);
          } else if (roll < 0.75) {
            setEmote(EMOTES[Math.floor(Math.random() * EMOTES.length)]);
            setTimeout(() => setEmote(null), 2200);
          }
        }, 4600);
        return next;
      });
    }, 12000);
    return () => clearInterval(iv);
  }, [mode, open]);

  /* generic teaser bubbles while idle (both modes) */
  useEffect(() => {
    if (open) { setTeaser(null); return; }
    const iv = setInterval(() => {
      setTeaser((t) => t || TEASERS[Math.floor(Math.random() * TEASERS.length)]);
      setTimeout(() => setTeaser(null), 4200);
    }, 23000);
    return () => clearInterval(iv);
  }, [open]);

  /* ACCOUNT-SYNCED look: the server copy wins (Nova looks the same on every
     device); localStorage stays as instant-boot cache. Writes go to both. */
  useEffect(() => {
    if (!user?.id) return;
    api.get("/buddy/profile").then((r) => {
      try {
        const a = JSON.parse(r.data?.appearance || "{}");
        if (a.color || a.eyes || a.acc) {
          const merged = { ...readLook(), ...a };
          setLook(merged);
          localStorage.setItem(LOOK_KEY, JSON.stringify(merged));
        }
      } catch { /* keep cached look */ }
    }).catch(() => {});
  }, [user?.id]);

  const saveLook = (next) => {
    setLook(next);
    try { localStorage.setItem(LOOK_KEY, JSON.stringify(next)); } catch {}
    api.put("/buddy/profile", { appearance: { color: next.color, eyes: next.eyes, acc: next.acc } }).catch(() => {});
  };

  if (!user) return null;

  const pos = mode === "wander" ? { left: ANCHORS[anchor].left, bottom: ANCHORS[anchor].bottom } : { right: "1.25rem", bottom: "5.5rem" };
  const avatarSize = mode === "wander" ? 116 : 84;

  return (
    <>
      <style>{`
        @keyframes nova-bob   { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes nova-aura  { 0%,100% { opacity: 0.75; transform: scale(1); } 50% { opacity: 1; transform: scale(1.06); } }
        @keyframes nova-hop   { 0%,100% { transform: translateY(0) rotate(0deg); } 30% { transform: translateY(-16px) rotate(${facing * 4}deg); } 60% { transform: translateY(-3px); } 80% { transform: translateY(-11px) rotate(${facing * 3}deg); } }
        @keyframes nova-land  { 0% { transform: scale(1.1, 0.82); } 55% { transform: scale(0.94, 1.06); } 100% { transform: scale(1, 1); } }
        @keyframes nova-in    { from { opacity: 0; transform: translateY(16px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes nova-bub   { 0% { opacity: 0; transform: translateY(6px) scale(0.9); } 12% { opacity: 1; transform: translateY(0) scale(1); } 88% { opacity: 1; } 100% { opacity: 0; } }
        @keyframes nova-dot   { 0%,80%,100% { transform: translateY(0); opacity: 0.4; } 40% { transform: translateY(-4px); opacity: 1; } }
        @keyframes nova-wave  { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(-38deg); } }
        @keyframes nova-emote { 0% { opacity: 0; transform: translateY(4px) scale(0.5); } 25% { opacity: 1; transform: translateY(-10px) scale(1.25); } 100% { opacity: 0; transform: translateY(-30px) scale(1); } }
        @keyframes nova-spark { 0% { opacity: 0.9; transform: translate(0,0) scale(1); } 100% { opacity: 0; transform: translate(var(--sx), 18px) scale(0.2); } }
      `}</style>

      {/* ── the living buddy ── */}
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="fixed z-[45]"
        title="Nova — dein Lern-Buddy"
        style={{
          ...pos,
          transition: mode === "wander" ? "left 4.6s cubic-bezier(0.45,0.05,0.35,1), bottom 4.6s cubic-bezier(0.45,0.05,0.35,1)" : undefined,
          background: "transparent", border: "none", cursor: "pointer", padding: 0,
        }}
      >
        {/* emote pop */}
        {emote && (
          <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-2xl" style={{ animation: "nova-emote 2.1s ease both" }}>{emote}</span>
        )}
        {/* speech bubble */}
        {teaser && !open && (
          <div className="absolute -top-14 left-1/2 w-max max-w-[230px] -translate-x-1/2 rounded-2xl px-4 py-2.5 text-xs font-bold leading-snug text-white"
            style={{ background: "rgba(14,8,36,0.94)", border: `1px solid ${look.color}55`, boxShadow: `0 0 24px ${look.color}35`, animation: "nova-bub 4.6s ease both", backdropFilter: "blur(8px)" }}>
            {teaser}
            <div className="absolute -bottom-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45" style={{ background: "rgba(14,8,36,0.94)", borderRight: `1px solid ${look.color}55`, borderBottom: `1px solid ${look.color}55` }} />
          </div>
        )}
        {/* sparkle trail while hopping */}
        {traveling && (
          <div className="pointer-events-none absolute inset-0">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className="absolute bottom-2 left-1/2 h-1.5 w-1.5 rounded-full"
                style={{ background: i % 2 ? "#fde047" : look.color, "--sx": `${(facing > 0 ? -1 : 1) * (14 + i * 9)}px`, animation: `nova-spark 0.8s ${i * 0.18}s ease-out infinite` }} />
            ))}
          </div>
        )}
        {/* hop while traveling · bob while idle · squish on land */}
        <div style={{
          animation: traveling ? "nova-hop 0.7s ease-in-out infinite" : "nova-bob 2.8s ease-in-out infinite",
          filter: hover ? `drop-shadow(0 0 26px ${look.color})` : "drop-shadow(0 10px 24px rgba(0,0,0,0.5))",
          transform: hover ? "scale(1.08)" : "scale(1)",
          transition: "transform 0.25s ease, filter 0.25s ease",
        }}>
          <div style={{ animation: !traveling ? "nova-land 0.45s ease both" : "none" }}>
            <NovaSprite size={avatarSize} fps={hover || traveling ? 16 : 11}
              facing={mode === "wander" ? facing : 1} aura={look.color} />
          </div>
        </div>
      </button>

      {open && <NovaChat look={look} onSaveLook={saveLook} userName={(user?.name || "").split(" ")[0]} onClose={() => setOpen(false)} />}
    </>
  );
}

/* ═══ THE CHAT PANEL (approved design — unchanged behaviour) ═════════════ */
function NovaChat({ look, onSaveLook, userName, onClose }) {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [style, setStyle] = useState("direkt");
  const listRef = useRef(null);

  const scrollDown = useCallback(() => {
    requestAnimationFrame(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; });
  }, []);

  useEffect(() => {
    api.get("/buddy/messages").then((r) => {
      setMessages(r.data.messages || []);
      setSessionId(r.data.sessionId);
      scrollDown();
    }).catch(() => {});
    api.get("/buddy/profile").then((r) => { const s = r.data?.style || r.data?.profile?.style; if (s) setStyle(s); }).catch(() => {});
  }, [scrollDown]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text, id: `u${Date.now()}` }]);
    setBusy(true);
    scrollDown();
    try {
      const r = await api.post("/buddy/chat", { message: text, sessionId });
      setSessionId(r.data.sessionId);
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, id: `a${Date.now()}` }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: e?.response?.data?.error || "Ups — Verbindung kurz weg. Probier's gleich nochmal! ✦", id: `e${Date.now()}` }]);
    } finally { setBusy(false); scrollDown(); }
  };

  const newSession = async () => {
    try {
      const r = await api.post("/buddy/session/new");
      setSessionId(r.data.session?.id || r.data.sessionId || null);
      setMessages([]);
    } catch {}
  };

  const saveStyle = async (s) => {
    setStyle(s);
    try { await api.put("/buddy/profile", { style: s }); } catch {}
  };

  return (
    <div className="fixed bottom-4 right-4 z-[66] flex w-[min(94vw,400px)] flex-col overflow-hidden rounded-3xl"
      style={{
        height: "min(72vh, 640px)",
        background: "linear-gradient(165deg, #171031 0%, #0b0522 60%, #070315 100%)",
        border: `1.5px solid ${look.color}50`,
        boxShadow: `0 0 60px ${look.color}25, 0 40px 90px rgba(0,0,0,0.7)`,
        animation: "nova-in 0.3s cubic-bezier(0.16,1,0.3,1) both",
      }}>
      {/* header */}
      <div className="relative flex items-center gap-3 px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: `linear-gradient(90deg, transparent, ${look.color}, #22d3ee, transparent)` }} />
        <NovaSprite size={48} fps={busy ? 16 : 10} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black text-white">Nova <span style={{ color: look.color }}>✦</span></p>
          <p className="text-[10px] font-bold text-white/40">{busy ? "denkt nach …" : "dein Lern-Buddy · immer da"}</p>
        </div>
        <button onClick={() => setCustomize((v) => !v)} title="Nova stylen"
          className={`grid h-8 w-8 place-items-center rounded-xl text-sm transition-all hover:scale-110 ${customize ? "bg-white/15" : "bg-white/5"}`}>🎨</button>
        <button onClick={newSession} title="Neues Gespräch"
          className="grid h-8 w-8 place-items-center rounded-xl bg-white/5 text-sm transition-all hover:scale-110">↺</button>
        <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-xl bg-white/5 text-sm text-white/70 transition-all hover:scale-110 hover:text-white">✕</button>
      </div>

      {/* customizer */}
      {customize && (
        <div className="space-y-3 px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }}>
          <div>
            <p className="mb-1.5 text-[9px] font-black uppercase tracking-widest text-white/40">Farbe</p>
            <div className="flex flex-wrap gap-1.5">
              {COLORS.map((c) => (
                <button key={c} onClick={() => onSaveLook({ ...look, color: c })}
                  className="h-7 w-7 rounded-full transition-transform hover:scale-110"
                  style={{ background: c, border: look.color === c ? "2.5px solid #fff" : "2.5px solid transparent", boxShadow: look.color === c ? `0 0 12px ${c}` : "none" }} />
              ))}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-[9px] font-black uppercase tracking-widest text-white/40">Wie soll Nova schreiben?</p>
            <div className="flex flex-wrap gap-1">
              {STYLES.map((s) => (
                <button key={s.id} onClick={() => saveStyle(s.id)}
                  className="rounded-lg px-2 py-1 text-[10px] font-bold transition-all"
                  style={style === s.id ? { background: `${look.color}30`, color: "#fff", border: `1px solid ${look.color}` } : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.6)", border: "1px solid transparent" }}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* messages */}
      <div ref={listRef} className="scrollbar-hide min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {messages.length === 0 && (
          <div className="mt-6 text-center">
            <div className="mx-auto w-fit"><NovaSprite size={110} fps={13} aura={look.color} /></div>
            <p className="mt-3 text-sm font-black text-white">Yo{userName ? ` ${userName}` : ""}! Ich bin Nova ✦</p>
            <p className="mx-auto mt-1 max-w-[260px] text-xs leading-relaxed text-white/50">
              Dein Lern-Buddy im StudoX-Universum. Frag mich Hausübungs-Fragen, lass dir was erklären oder hol dir einen Lern-Plan. Ich bin für dich da — fr. 💜
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-1.5">
              {["Erklär mir Brüche 🍰", "Mach mir einen Lernplan 📅", "Motivier mich 🔥"].map((s) => (
                <button key={s} onClick={() => { setInput(s.replace(/ [^ ]+$/, "")); }}
                  className="rounded-full px-3 py-1.5 text-[11px] font-bold text-white/75 transition-all hover:scale-105"
                  style={{ background: `${look.color}18`, border: `1px solid ${look.color}40` }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id || m.createdAt} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role !== "user" && <div className="mr-2 mt-0.5 shrink-0"><NovaSprite size={30} playing={false} /></div>}
            <div className={`max-w-[78%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${m.role === "user" ? "text-white" : "text-white/90"}`}
              style={m.role === "user"
                ? { background: `linear-gradient(135deg, ${look.color}, ${look.color}aa)`, borderBottomRightRadius: 6, boxShadow: `0 4px 14px ${look.color}30` }
                : { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", borderBottomLeftRadius: 6 }}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="mr-2 mt-0.5 shrink-0"><NovaSprite size={30} fps={14} /></div>
            <div className="flex items-center gap-1 rounded-2xl px-4 py-3" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>
              {[0, 1, 2].map((i) => (
                <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ background: look.color, animation: `nova-dot 1.1s ${i * 0.18}s infinite` }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* input */}
      <div className="flex items-center gap-2 px-3 py-3" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Schreib Nova …"
          className="min-w-0 flex-1 rounded-2xl px-4 py-2.5 text-sm text-white outline-none placeholder:text-white/30"
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
        />
        <button onClick={send} disabled={busy || !input.trim()}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-40"
          style={{ background: `linear-gradient(135deg, ${look.color}, #22d3ee)`, boxShadow: `0 0 18px ${look.color}40` }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13" /><path d="M22 2 15 22 11 13 2 9 22 2" /></svg>
        </button>
      </div>
    </div>
  );
}
